import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, number, object } from 'yup'

import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { TpvsContract } from '@/Store/application'
import { TpvModel, TpvsRepository } from '@/Store/domain'

import { removeFirstZeroFromTPV } from '../services/tpvServices'
import { tpvSchema } from '@/Shared/ui/form/validations/tpvSchema'
import { MetaStatusCodes } from '@/Shared/domain/enum/MetaStatusCodes'
import { NotificationSeverity } from '@/Shared/domain/enum/notification'
import { dataToExcel } from '@/Shared/domain/helpers'

export interface TpvForm {
  terminalMid: string
  merchantTargetId: number
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<TpvForm> =>
  object({
    terminalMid: tpvSchema(t).required(),
    merchantTargetId: number().required(),
  })

export interface IUseTpvController {
  form: UseFormReturn<TpvForm>
  model: TpvModel[]
  addNewTpv: (data: TpvForm) => Promise<boolean>
  loadTpvs: () => Promise<void>
  downloadTpvsDataExcel: () => Promise<void>
}

export const useTpvController = (
  repository: TpvsRepository,
  id: number,
): IUseTpvController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()

  const [model, setModel] = useState<TpvModel[]>([])

  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<TpvForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onChange',
    defaultValues: {
      terminalMid: '',
      merchantTargetId: id,
    },
  })

  const loadTpvs = async (): Promise<void> => {
    startLoading()

    const response = await TpvsContract(repository).getTpvsData(id)

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      if (response.data) setModel(response.data)
    }
  }

  const addNewTpv = async (data: TpvForm): Promise<boolean> => {
    startLoading()

    const response = await TpvsContract(repository).addNewTpv({
      terminalMid: removeFirstZeroFromTPV(data.terminalMid),
      merchantTargetId: data.merchantTargetId,
    })

    stopLoading()

    const valid = handleMetaResponse(response?.meta, undefined, { notifySuccess: true })

    if (valid) {
      loadTpvs()
    }

    return valid
  }

  const downloadTpvsDataExcel = async (): Promise<void> => {
    startLoading()
    const response = await TpvsContract(repository).getTpvsDataExcel(id)
    if (response?.meta?.status === MetaStatusCodes.SUCCESS) {
      dataToExcel(response?.data?.file || '', response?.data?.fileName || 'terminales.xlsx')
    } else {
      handleMetaResponse(response?.meta, undefined, { notifySuccess: false })
    }
    stopLoading()
  }

  return {
    form,
    model,
    addNewTpv,
    loadTpvs,
    downloadTpvsDataExcel,
  }
}
